/**
 * ZeroResultSection — расход, не давший обращений.
 *
 * В отличие от денег под риском, здесь величины периодные: это факт за
 * месяц, а не проекция суточной цифры. Умножения нет нигде.
 *
 * Текстов модели в блоке не приходит, и придумывать их нечем. Зато
 * клики рассказывают историю сами: деньги ушли, люди пришли, обращений
 * нет. `kpiFact` в этом блоке по определению пуст — кампания попала сюда
 * именно потому, что результата нет, — поэтому не показывается.
 *
 * Строковые примитивы строки (`SpendRowAmount`/`SpendRowName`/`SpendRowMeta`)
 * общие с `RisksSection` — обе секции устроены одинаково, и объявлять их
 * здесь заново значило бы дублировать `SpendBlock.tsx` слово в слово.
 *
 * @format
 */

import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';

import {TOP_ROWS} from '../content';
import {useReportFormat} from '../format';
import {CLICKS, pluralize} from '../helpers';
import type {Waste, WasteCampaign} from '../types';
import {SpendBlock, SpendRowAmount, SpendRowMeta, SpendRowName} from './SpendBlock';
import {SplitCard, SplitPanel} from './SplitCard';

export interface ZeroResultSectionProps {
  data: Waste;
  /** Расход всех кампаний за период — знаменатель доли. */
  totalSpend: number;
}

/** Неразрывный пробел: escape, а не живой символ — его стирает eslint --fix. */
const NBSP = '\u00A0';

function WasteLine({campaign}: {campaign: WasteCampaign}) {
  const fmt = useReportFormat();

  return (
    <SplitPanel padding='l'>
      {/* `nowrap` (по умолчанию) — не `wrap`: соседний `Stack` по контракту
          занимает 100% ширины контейнера (Stack.tsx:58), и с `wrap` браузер
          переносил бы его на новую строку вместо того, чтобы сжать до
          остатка ширины после колонки суммы. `reflowAt='md'` сворачивает
          ряд в столбец на узком экране — там имени и подписи нужна полная
          ширина, а не сжатая полоса рядом с суммой. */}
      <Row gap='m' alignY='start' reflowAt='md'>
        <SpendRowAmount>{fmt.money(campaign.spend ?? 0)}</SpendRowAmount>
        <Stack gap='xxs'>
          <SpendRowName>{campaign.name || campaign.campaignId}</SpendRowName>
          {campaign.clicks != null && (
            // «одного обращения» — последние два слова фразы длиннее двух
            // слов (typography.md §1) — NBSP между ними.
            <SpendRowMeta>{`${pluralize(campaign.clicks, CLICKS)}, ни${NBSP}одного${NBSP}обращения`}</SpendRowMeta>
          )}
        </Stack>
      </Row>
    </SplitPanel>
  );
}

export function ZeroResultSection({data, totalSpend}: ZeroResultSectionProps) {
  // Строка без расхода не показывается: сумма — причина, по которой
  // кампания вообще попала в список (тот же фильтр, что и в
  // `RisksSection`). Фильтр стоит до срезки — иначе пустая строка съедала
  // бы один из пяти слотов `TOP_ROWS`, вместо него не показывая ничего.
  const campaigns = (data.campaigns ?? []).filter(
    campaign => campaign.spend !== null && campaign.spend !== undefined,
  );
  // Список приходит отсортированным по убыванию расхода: первые идут
  // строками, остальные сворачиваются в одну фразу.
  const top = campaigns.slice(0, TOP_ROWS);
  const rest = campaigns.slice(TOP_ROWS);
  const restSum = rest.reduce((sum, campaign) => sum + (campaign.spend ?? 0), 0);
  // `data.spend ?? 0` печатал бы «0 ₽» в заголовке над строками настоящих
  // денег, если сервер прислал сумму пустой. Считаем сами по кампаниям —
  // те же деньги, что и в строках, просто сложенные.
  const amount = data.spend ?? campaigns.reduce((sum, campaign) => sum + (campaign.spend ?? 0), 0);

  return (
    <SpendBlock
      amount={amount}
      of={totalSpend}
      share={data.pct}
      // «за» (предлог) липнет к «период» (§2); «период отчёта» — последние
      // два слова фразы длиннее двух слов (§1) — NBSP и там: обе связки
      // покрывают всю подпись целиком.
      caption={`За${NBSP}период${NBSP}отчёта`}
      rest={
        rest.length > 0
          ? {
              sum: restSum,
              count: rest.length,
              rows: rest.map(campaign => ({
                key: campaign.campaignId,
                name: campaign.name || campaign.campaignId,
                spend: campaign.spend ?? 0,
              })),
            }
          : null
      }
    >
      <SplitCard direction='column' reflowAt='never'>
        {top.map(campaign => (
          <WasteLine key={campaign.campaignId} campaign={campaign} />
        ))}
      </SplitCard>
    </SpendBlock>
  );
}
